import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getGraphClient } from "../services/graphClient";
import {
  getDriveId,
  listVendorFolders,
  listPOsForVendor,
  uploadPO,
  movePO,
  getComments,
  postComment,
  postCommentWithMentions,
  ensureCommentsList,
  updatePOStatus,
  createVendorFolder,
  searchPOs,
  moveToClosedFolder,
} from "../services/sharepoint";

export function useSharePoint() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [siteId, setSiteId] = useState(null);
  const [driveId, setDriveId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [pos, setPOs] = useState([]);
  const [comments, setComments] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [loading, setLoading] = useState({ init: true, pos: false, comments: false });
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchScope, setSearchScope] = useState("all");
  const [searching, setSearching] = useState(false);

  const client = useCallback(
    () => getGraphClient(instance, account),
    [instance, account]
  );

  useEffect(() => {
    if (!account) return;
    (async () => {
      try {
        const c = client();
        const { siteId: sid, driveId: did } = await getDriveId(c);
        setSiteId(sid);
        setDriveId(did);
        await ensureCommentsList(c, sid);
        const folders = await listVendorFolders(c, did);
        setVendors(folders);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading((l) => ({ ...l, init: false }));
      }
    })();
  }, [account, client]);

  useEffect(() => {
    if (!driveId || !selectedVendor) return;
    setLoading((l) => ({ ...l, pos: true }));
    setPOs([]);
    setSelectedPO(null);
    (async () => {
      try {
        const items = await listPOsForVendor(client(), driveId, selectedVendor);
        setPOs(items);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading((l) => ({ ...l, pos: false }));
      }
    })();
  }, [driveId, selectedVendor, client]);

  useEffect(() => {
    if (!siteId || !selectedPO) return;
    setLoading((l) => ({ ...l, comments: true }));
    (async () => {
      try {
        const c = await getComments(client(), siteId, selectedPO.name);
        setComments(c);
      } catch {
        setComments([]);
      } finally {
        setLoading((l) => ({ ...l, comments: false }));
      }
    })();
  }, [siteId, selectedPO, client]);

  const handleUpload = useCallback(
    async (file) => {
      if (!driveId || !selectedVendor) return;
      await uploadPO(client(), driveId, selectedVendor, file);
      const items = await listPOsForVendor(client(), driveId, selectedVendor);
      setPOs(items);
    },
    [driveId, selectedVendor, client]
  );

  const handleMove = useCallback(
    async (fileId, targetVendor) => {
      if (!driveId) return;
      await movePO(client(), driveId, fileId, targetVendor);
      const items = await listPOsForVendor(client(), driveId, selectedVendor);
      setPOs(items);
      setSelectedPO(null);
    },
    [driveId, selectedVendor, client]
  );

  const handlePostComment = useCallback(
    async (text, mentionedEmails = []) => {
      if (!siteId || !selectedPO) return;
      // Determine the vendor folder: prefer the PO's own vendorName (set in
      // status views), then its parent folder name, then the selected vendor.
      const vendorFolder =
        selectedPO.vendorName ||
        selectedPO.parentReference?.name ||
        selectedVendor ||
        "";
      await postCommentWithMentions(
        client(), siteId, selectedPO.name, vendorFolder,
        text, account.name, mentionedEmails
      );
      const updated = await getComments(client(), siteId, selectedPO.name);
      setComments(updated);
    },
    [siteId, selectedPO, selectedVendor, account, client]
  );

  // Writes an automated audit entry into the comments list.
  // The SYSTEM_PREFIX marker lets the UI render these differently from
  // real comments. No MentionedEmails is set, so the Power Automate
  // notification flow does not fire for routine status changes.
  const logStatusChange = useCallback(
    async (po, newStatus) => {
      if (!siteId || !po || !account) return;
      const vendorFolder =
        po.vendorName ||
        po.parentReference?.name ||
        selectedVendor ||
        "";
      try {
        await postComment(
          client(), siteId, po.name, vendorFolder,
          `[status] Marked as ${newStatus}`, account.name
        );
      } catch (e) {
        // Logging must never block the status change itself
        console.error("[logStatusChange] error:", e);
      }
    },
    [siteId, selectedVendor, account, client]
  );

  const handleStatusChange = useCallback(
    async (fileId, newStatus, poRef) => {
      if (!driveId) return;

      // Resolve the PO so we can record its name/vendor in the log entry.
      // Status views pass the PO directly; other views we look up locally.
      const po =
        poRef ||
        pos.find((p) => p.id === fileId) ||
        (selectedPO?.id === fileId ? selectedPO : null);

      if (newStatus === "Closed / Invoiced") {
        try { await updatePOStatus(client(), driveId, fileId, "Closed / Invoiced"); } catch {}
        // Log before the move, while we still hold the PO details
        await logStatusChange(po, newStatus);
        await moveToClosedFolder(client(), driveId, fileId);
        setPOs((prev) => prev.filter((p) => p.id !== fileId));
        if (selectedPO?.id === fileId) setSelectedPO(null);
        return;
      }

      await updatePOStatus(client(), driveId, fileId, newStatus);
      await logStatusChange(po, newStatus);

      const items = await listPOsForVendor(client(), driveId, selectedVendor);
      setPOs(items);
      if (selectedPO) {
        const updated = items.find((p) => p.id === selectedPO.id);
        if (updated) setSelectedPO(updated);
        // Refresh the thread so the new entry appears right away
        if (selectedPO.id === fileId && siteId) {
          try {
            const refreshed = await getComments(client(), siteId, selectedPO.name);
            setComments(refreshed);
          } catch {}
        }
      }
    },
    [driveId, selectedVendor, selectedPO, pos, siteId, client, logStatusChange]
  );

  const handleAddVendor = useCallback(
    async (folderName) => {
      if (!driveId) return;
      await createVendorFolder(client(), driveId, folderName);
      const folders = await listVendorFolders(client(), driveId);
      setVendors(folders);
      setSelectedVendor(folderName);
    },
    [driveId, client]
  );

  const handleSearch = useCallback(
    async (query) => {
      if (!driveId || !query || !query.trim()) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const scope = searchScope === "vendor" ? selectedVendor : null;
        const results = await searchPOs(client(), driveId, query.trim(), scope);
        setSearchResults(results);
      } catch (e) {
        setError(e.message);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [driveId, selectedVendor, searchScope, client]
  );

  const handleRefresh = useCallback(async () => {
    if (!driveId) return;
    setLoading((l) => ({ ...l, pos: true }));
    try {
      const folders = await listVendorFolders(client(), driveId);
      setVendors(folders);
      if (selectedVendor) {
        const items = await listPOsForVendor(client(), driveId, selectedVendor);
        setPOs(items);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading((l) => ({ ...l, pos: false }));
    }
  }, [driveId, selectedVendor, client]);

  return {
    vendors, pos, comments, selectedVendor, setSelectedVendor,
    selectedPO, setSelectedPO, loading, error,
    handleUpload, handleMove, handlePostComment,
    handleStatusChange, handleAddVendor,
    searchResults, searchScope, setSearchScope,
    searching, handleSearch,
    handleRefresh, driveId, account,
  };
}
